"use client";

import React, { useState } from "react";
import { ArrowRight, Maximize2, MapPin, House, Building2, Paintbrush } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Project {
  id: string;
  title: string;
  category: "Residential" | "Commercial";
  location: string;
  description: string;
  image: string;
  aspectRatio: number;
}
const projects: Project[] = [{
  id: "res-1",
  title: "Magnetic Island Retreat",
  category: "Residential",
  location: "Magnetic Island, QLD",
  description: "A complete exterior restoration of a luxury holiday home, utilizing salt-resistant premium coatings to withstand the coastal elements while enhancing the tropical aesthetic.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559587/img/magnetic_island_retreat.png",
  aspectRatio: 16 / 9
}, {
  id: "com-1",
  title: "Townsville CBD Law Chambers",
  category: "Commercial",
  location: "Townsville City, QLD",
  description: "Interior revitalization of a heritage-listed office building. We carefully restored original plasterwork and applied a modern, sophisticated color palette to professional spaces.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559587/img/townsville_cbd_law_chambers.png",
  aspectRatio: 4 / 3
}, {
  id: "res-2",
  title: "Award winning Home in Annandale",
  category: "Residential",
  location: "Annandale, QLD",
  description: "Meticulous preparation and painting of a stunning modern home. The project involved lead paint remediation and the application of high-gloss enamel finishes to verandas.",
  image: "https://mature-leech.10web.cloud/wp-content/uploads/2026/02/12.avif?v=d23yxvutdbr",
  aspectRatio: 3 / 4
}, {
  id: "com-2",
  title: "The Dalrymple Hotel",
  category: "Commercial",
  location: "The Strand, Townsville",
  description: "A vibrant exterior refresh for a boutique hotel. High-durability, UV-resistant paints were selected to maintain color integrity against the harsh North Queensland sun.",
  image: "https://mature-leech.10web.cloud/wp-content/uploads/2026/02/20240820_215425836_iOS-scaled.jpg?v=k4gmdhs5oa",
  aspectRatio: 16 / 9
}, {
  id: "res-3",
  title: "Castle Hill Modernist Villa",
  category: "Residential",
  location: "Castle Hill, QLD",
  description: "Interior and exterior painting for a contemporary architectural masterpiece. We used low-VOC paints to ensure healthy indoor air quality for the family.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559587/img/castle_hill_modernist_villa.png",
  aspectRatio: 4 / 3
}, {
  id: "com-3",
  title: "Queens Hotel Ayr",
  category: "Commercial",
  location: "Ayr, QLD",
  description: "Creating an inviting atmosphere for a popular dining venue. The project required after-hours work to minimize business disruption, delivering a flawless finish on time.",
  image: "https://mature-leech.10web.cloud/wp-content/uploads/2026/02/505529833_1190106519795874_4430647418272172695_n.jpg?v=6ege2ih3j1",
  aspectRatio: 3 / 4
}];
export default function RegionalTransformations() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const [activeTab, setActiveTab] = useState("all");
  const filteredProjects = activeTab === "all" ? projects : projects.filter(p => p.category.toLowerCase() === activeTab);
  return <section id="regional-transformations" className="bg-background py-24 sm:py-32 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-primary font-medium tracking-wider uppercase text-sm mb-3">
            Our Portfolio
          </h2>
          <h3 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-6 tracking-tight">
            Regional Transformations
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Explore our gallery of elite craftsmanship across North Queensland. 
            From heritage restorations in Townsville to modern commercial finishes, 
            witness the impact of professional surface preparation and premium coatings.
          </p>
        </div>

        {}
        <Tabs defaultValue="all" className="w-full flex flex-col items-center" onValueChange={setActiveTab}>
          <TabsList className="mb-12 bg-muted/50 p-1 rounded-full border border-border/50">
            <TabsTrigger value="all" className="rounded-full px-6 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
              All Projects
            </TabsTrigger>
            <TabsTrigger value="residential" className="rounded-full px-6 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
              Residential
            </TabsTrigger>
            <TabsTrigger value="commercial" className="rounded-full px-6 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
              Commercial
            </TabsTrigger>
          </TabsList>

          {}
          <div className="w-full columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
            {filteredProjects.map((project, index) => <div key={project.id} data-index={index} className="break-inside-avoid group relative rounded-xl overflow-hidden bg-card border border-border/40 shadow-sm hover:shadow-md transition-all duration-500">
                <Dialog>
                  <DialogTrigger asChild>
                    <div className="cursor-pointer relative overflow-hidden">
                      <AspectRatio ratio={project.aspectRatio}>
                        <img src={project.image} alt={project.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" />
                      </AspectRatio>
                      
                      {}
                      <div className="absolute inset-0 bg-primary/90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center text-white p-6 text-center">
                        <Maximize2 className="w-8 h-8 mb-3 opacity-0 group-hover:translate-y-0 translate-y-4 transition-all duration-500 delay-100" />
                        <h4 className="text-xl font-bold font-serif opacity-0 group-hover:translate-y-0 translate-y-4 transition-all duration-500 delay-150">
                          {project.title}
                        </h4>
                        <span className="text-sm font-medium mt-2 opacity-0 group-hover:translate-y-0 translate-y-4 transition-all duration-500 delay-200">
                          View Project Details
                        </span>
                      </div>
                    </div>
                  </DialogTrigger>
                  
                  {}
                  <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden bg-background border-none shadow-2xl sm:rounded-2xl">
                    <div className="grid grid-cols-1 lg:grid-cols-5 h-full max-h-[90vh] overflow-y-auto lg:overflow-hidden">
                      
                      {}
                      <div className="lg:col-span-3 bg-muted relative min-h-[300px] lg:h-full">
                        <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
                      </div>

                      {}
                      <div className="lg:col-span-2 p-8 flex flex-col justify-center bg-card">
                        <DialogHeader className="mb-6 text-left">
                          <div className="flex items-center gap-2 text-primary text-sm font-bold uppercase tracking-wider mb-2">
                            {project.category === "Residential" ? <House className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                            {project.category}
                          </div>
                          <DialogTitle className="text-3xl font-serif font-bold text-foreground mb-2">
                            {project.title}
                          </DialogTitle>
                          <div className="flex items-center text-muted-foreground text-sm">
                            <MapPin className="w-4 h-4 mr-1" />
                            {project.location}
                          </div>
                        </DialogHeader>

                        <DialogDescription className="text-base text-muted-foreground leading-relaxed mb-8">
                          {project.description}
                        </DialogDescription>

                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="bg-primary/10 p-2 rounded-full text-primary mt-1">
                              <Paintbrush className="w-4 h-4" />
                            </div>
                            <div>
                              <h5 className="font-semibold text-foreground text-sm">Premium Finishes</h5>
                              <p className="text-xs text-muted-foreground">High-quality, low-VOC paints used for lasting durability.</p>
                            </div>
                          </div>
                        </div>

                        <DialogFooter className="mt-8 sm:justify-start gap-3">
                          <DialogClose asChild>
                            <Button variant="outline" className="w-full sm:w-auto">
                              Close
                            </Button>
                          </DialogClose>
                          <Button asChild className="w-full sm:w-auto">
                            <Link to="/GetAQuotePage">
                              Get a Quote <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                          </Button>
                        </DialogFooter>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {}
                <div className="p-5 bg-card">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold text-primary uppercase tracking-wider">
                      {project.category}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-foreground font-serif mb-1 group-hover:text-primary transition-colors">
                    {project.title}
                  </h4>
                  <p className="text-sm text-muted-foreground flex items-center">
                    <MapPin className="w-3 h-3 mr-1" /> {project.location}
                  </p>
                </div>
              </div>)}
          </div>
        </Tabs>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="rounded-full px-8">
            <Link to="/WorkGalleryPage">
              View Full Gallery <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}